/**
 * ShareCard.tsx — Canvas-based Instagram Stories share card
 * Renders at 2x resolution for crisp display on all phones.
 * Draws the post exactly as it appears in the feed.
 */

import { useEffect, useRef, useState } from 'react'
import { getFillLabel } from '../../lib/mugScale'
import { X, Download, Share2 } from 'lucide-react'
import { supabaseUrl, supabaseAnonKey } from '../../lib/supabase'

interface Rating {
  id: string
  fill_level: number
  caption?: string | null
  drink_name?: string | null
  drink_size?: string | null
  vibe_tags?: string[]
  photo_url?: string | null
  photo_urls?: string[]
  visit_time?: string | null
  is_quick_sip?: boolean
  drink_price?: number | null
  price_perception?: string | null
  show_price?: boolean | null
  likes_count?: number | null
  comments_count?: number | null
  created_at?: string | null
  is_first_rating?: boolean | null
  coffee_shops?: {
    name: string
    city?: string | null
    state?: string | null
    country?: string | null
    photo_url?: string | null
  }
  profiles?: {
    username: string
    avatar_url?: string | null
    badge?: string | null
    verified?: boolean | null
  }
}

interface Props {
  rating: Rating
  onClose: () => void
}

const APP_URL = 'https://socialbrewapp.com'
const SCALE = 2 // retina quality

// Mirrors HomeTab's timeAgo / formatDate so the card reads like the feed
function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// getFillLabel now comes from the shared canonical mug scale (imported above).

function getLiquidColor(fill: number) {
  if (fill === 100) return '#3d1a06'
  if (fill >= 90) return '#6b3410'
  if (fill >= 80) return '#9b5e1a'
  if (fill >= 70) return '#b87333'
  if (fill >= 60) return '#c49a6c'
  return '#d4b896'
}

function formatLocation(
  city?: string | null,
  state?: string | null,
  country?: string | null
): string {
  const c = city?.trim()
  const s = state?.trim()
  const co = country?.trim()
  if (!c) return s || co || ''
  if (s && (!co || co === 'United States')) return `${c}, ${s}`
  if (co && co !== 'United States') return `${c}, ${co}`
  return c
}

async function loadImage(url: string): Promise<HTMLImageElement | null> {
  const attempts = [
    `${supabaseUrl}/functions/v1/image-proxy?url=${encodeURIComponent(url)}&apikey=${supabaseAnonKey}`,
    url,
  ]
  for (const src of attempts) {
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject()
        img.src = src
        setTimeout(() => reject(), 5000)
      })
      if (img.naturalWidth > 0) return img
    } catch { /* try next */ }
  }
  return null
}

// Same-origin logo/asset loader — no proxy, no CORS taint on the canvas
function loadLocalImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve(img.naturalWidth > 0 ? img : null)
    img.onerror = () => resolve(null)
    img.src = src
    setTimeout(() => resolve(null), 4000)
  })
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  w: number, h: number,
  r: number
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current)
      current = word
    } else {
      current = test
    }
  }
  if (current) lines.push(current)
  return lines
}

function drawMug(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  size: number,
  fill: number
) {
  // Matches the feed SVG exactly: viewBox="0 0 56 68"
  // sc maps SVG units → canvas pixels
  const sc = size / 56
  const liquidColor = getLiquidColor(fill)

  // Saucer ellipse: cx=24 cy=58 rx=19 ry=5
  ctx.beginPath()
  ctx.ellipse(x + 24 * sc, y + 58 * sc, 19 * sc, 5 * sc, 0, 0, Math.PI * 2)
  ctx.fillStyle = '#e8ddc8'
  ctx.fill()

  // Body background (cream): x=5 y=12 w=38 h=46 rx=5
  roundRect(ctx, x + 5 * sc, y + 12 * sc, 38 * sc, 46 * sc, 5 * sc)
  ctx.fillStyle = '#f7f0e4'
  ctx.fill()

  // Liquid fill clipped to body
  if (fill > 0) {
    ctx.save()
    roundRect(ctx, x + 5 * sc, y + 12 * sc, 38 * sc, 46 * sc, 5 * sc)
    ctx.clip()
    const fillH = 46 * sc * fill / 100
    const fillY = y + 58 * sc - fillH
    ctx.fillStyle = liquidColor
    ctx.fillRect(x + 5 * sc, fillY, 38 * sc, fillH)
    ctx.restore()
  }

  // Body stroke only (liquid visible through it)
  roundRect(ctx, x + 5 * sc, y + 12 * sc, 38 * sc, 46 * sc, 5 * sc)
  ctx.strokeStyle = '#c8b090'
  ctx.lineWidth = 1.5 * sc
  ctx.stroke()

  // Rim: x=3 y=8 w=42 h=8 rx=4
  roundRect(ctx, x + 3 * sc, y + 8 * sc, 42 * sc, 8 * sc, 4 * sc)
  ctx.fillStyle = '#d4b890'
  ctx.fill()

  // Handle: M43 22 Q56 22 56 33 Q56 44 43 44
  ctx.beginPath()
  ctx.moveTo(x + 43 * sc, y + 22 * sc)
  ctx.quadraticCurveTo(x + 56 * sc, y + 22 * sc, x + 56 * sc, y + 33 * sc)
  ctx.quadraticCurveTo(x + 56 * sc, y + 44 * sc, x + 43 * sc, y + 44 * sc)
  ctx.strokeStyle = '#c8b090'
  ctx.lineWidth = 5 * sc
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke()
}

function drawAvatar(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  cx: number, cy: number,
  r: number,
  initial: string,
  color: string
) {
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.clip()

  if (img) {
    // Cover fit
    const size = r * 2
    const ratio = Math.max(size / img.naturalWidth, size / img.naturalHeight)
    const dw = img.naturalWidth * ratio
    const dh = img.naturalHeight * ratio
    ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh)
  } else {
    ctx.fillStyle = color
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2)
    ctx.font = `bold ${r}px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = 'white'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(initial.toUpperCase(), cx, cy)
    ctx.textBaseline = 'alphabetic'
    ctx.textAlign = 'left'
  }

  ctx.restore()

  // Ring
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.strokeStyle = '#e8d4b0'
  ctx.lineWidth = 1.5
  ctx.stroke()
}

// Verified badge — mirrors VerifiedBadge.tsx: 8-spike teal star + cream check
function drawVerifiedBadge(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  size: number
) {
  const sc = size / 24 // VerifiedBadge uses a 24x24 viewBox
  const starCX = 12, starCY = 12
  const outerR = 11, innerR = 7.8
  const spikes = 8

  ctx.save()
  ctx.translate(cx - 12 * sc, cy - 12 * sc)
  ctx.scale(sc, sc)

  ctx.beginPath()
  for (let i = 0; i < spikes * 2; i++) {
    const angle = (i * Math.PI) / spikes - Math.PI / 2
    const r = i % 2 === 0 ? outerR : innerR
    const px = starCX + r * Math.cos(angle)
    const py = starCY + r * Math.sin(angle)
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
  const grad = ctx.createLinearGradient(0, 0, 24, 24)
  grad.addColorStop(0, '#34d399')
  grad.addColorStop(1, '#0d9488')
  ctx.fillStyle = grad
  ctx.fill()

  // Checkmark
  ctx.beginPath()
  ctx.moveTo(7.5, 12.2)
  ctx.lineTo(10.8, 15.5)
  ctx.lineTo(16.5, 8.5)
  ctx.strokeStyle = '#fdf6ee'
  ctx.lineWidth = 2.4
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke()

  ctx.restore()
}

// Heart outline — matches the feed's like icon
function drawHeart(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  size: number,
  color: string
) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.beginPath()
  ctx.moveTo(12, 21)
  ctx.bezierCurveTo(12, 21, 3, 15, 3, 8.5)
  ctx.bezierCurveTo(3, 5.4, 5.4, 3, 8.5, 3)
  ctx.bezierCurveTo(10.3, 3, 12, 4.2, 12, 4.2)
  ctx.bezierCurveTo(12, 4.2, 13.7, 3, 15.5, 3)
  ctx.bezierCurveTo(18.6, 3, 21, 5.4, 21, 8.5)
  ctx.bezierCurveTo(21, 15, 12, 21, 12, 21)
  ctx.closePath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()
  ctx.restore()
}

// Comment bubble outline — matches the feed's comment icon
function drawComment(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  size: number,
  color: string
) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.beginPath()
  ctx.moveTo(21, 11.5)
  ctx.bezierCurveTo(21, 15.9, 16.9, 19.5, 12, 19.5)
  ctx.bezierCurveTo(10.9, 19.5, 9.9, 19.3, 8.9, 19)
  ctx.lineTo(3, 20.5)
  ctx.lineTo(4.6, 15.2)
  ctx.bezierCurveTo(3.6, 14.1, 3, 12.9, 3, 11.5)
  ctx.bezierCurveTo(3, 7.1, 7.1, 3.5, 12, 3.5)
  ctx.bezierCurveTo(16.9, 3.5, 21, 7.1, 21, 11.5)
  ctx.closePath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()
  ctx.restore()
}

// Bookmark / save outline
function drawBookmark(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number, color: string
) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.beginPath()
  ctx.moveTo(6, 3)
  ctx.lineTo(18, 3)
  ctx.lineTo(18, 21)
  ctx.lineTo(12, 16)
  ctx.lineTo(6, 21)
  ctx.closePath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()
  ctx.restore()
}

// Share (three nodes) outline
function drawShareIcon(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number, color: string
) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  // connectors
  ctx.beginPath()
  ctx.moveTo(8.6, 13.5); ctx.lineTo(15.4, 17.5)
  ctx.moveTo(15.4, 6.5); ctx.lineTo(8.6, 10.5)
  ctx.stroke()
  // nodes
  const dot = (x: number, y: number) => { ctx.beginPath(); ctx.arc(x, y, 2.6, 0, Math.PI * 2); ctx.stroke() }
  dot(18, 5); dot(6, 12); dot(18, 19)
  ctx.restore()
}

// Plus outline
function drawPlus(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, size: number, color: string
) {
  const s = size / 24
  ctx.save()
  ctx.strokeStyle = color
  ctx.lineWidth = 2 * s
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(cx - 6 * s, cy); ctx.lineTo(cx + 6 * s, cy)
  ctx.moveTo(cx, cy - 6 * s); ctx.lineTo(cx, cy + 6 * s)
  ctx.stroke()
  ctx.restore()
}

export default function ShareCard({ rating, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [generating, setGenerating] = useState(true)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [sharing, setSharing] = useState(false)
  const [status, setStatus] = useState<string | null>(null)

  const shop = rating.coffee_shops
  const user = rating.profiles
  const fill = rating.fill_level || 0
  const isQuickSip = rating.is_quick_sip === true
  const isVibePost = fill === 0 && !isQuickSip
  const showPrice = rating.show_price !== false && (rating.drink_price || rating.price_perception)
  const photos = (
    rating.photo_urls?.length
      ? rating.photo_urls
      : rating.photo_url ? [rating.photo_url] : []
  ).filter(Boolean) as string[]
  const liquidColor = getLiquidColor(fill)
  const fillLabel = getFillLabel(fill)
  const location = formatLocation(shop?.city, shop?.state, shop?.country)

  useEffect(() => { generateCard() }, [])

  async function generateCard() {
    setGenerating(true)
    setStatus(null)
    const canvas = canvasRef.current
    if (!canvas) { setGenerating(false); return }

    // ── Layout constants (logical pixels, drawn at SCALE) ───
    const W = 390
    const PAD = 20
    const CARD_MX = 16        // card horizontal margin
    const CARD_W = W - CARD_MX * 2
    const CARD_R = 20         // card corner radius
    const HEADER_H = 76
    const CARD_PAD_TOP = 16
    const AVATAR_R = 20       // avatar radius
    const AVATAR_ROW_H = 48
    const DRINK_ROW_H = (rating.drink_name || showPrice) ? 38 : 0
    const MUG_SIZE = 72
    const MUG_ROW_H = (!isVibePost && fill > 0) ? MUG_SIZE + 16 : 0
    const BADGE_ROW_H = (isQuickSip || isVibePost) ? 34 : 0

    // Load images in parallel (logo is same-origin, avatar/photos may be remote)
    const [avatarImg, logoImg, shopImg, ...photoImgs] = await Promise.all([
      user?.avatar_url ? loadImage(user.avatar_url) : Promise.resolve(null),
      loadLocalImage('/icon-192.png'),
      shop?.photo_url ? loadImage(shop.photo_url) : Promise.resolve(null),
      ...photos.slice(0, 4).map(u => loadImage(u)),
    ])

    const loadedPhotos = photoImgs.filter(Boolean) as HTMLImageElement[]
    const PHOTO_H = loadedPhotos.length > 0
      ? (loadedPhotos.length === 1 ? 260 : loadedPhotos.length === 2 ? 220 : 300)
      : 0

    const TAGS_H = (rating.vibe_tags?.length || 0) > 0 ? 44 : 0

    // Pre-measure caption lines
    const tmpCtx = document.createElement('canvas').getContext('2d')!
    tmpCtx.font = `14px system-ui, -apple-system, sans-serif`
    const captionLines = rating.caption
      ? wrapText(tmpCtx, rating.caption, CARD_W - PAD * 2)
      : []
    const CAPTION_H = captionLines.length > 0
      ? Math.min(captionLines.length, 6) * 22 + 20
      : 0

    const SHOP_H = shop ? 58 : 0
    const ACTIONS_H = 42    // like / comment / reaction / wishlist / gift / save / share
    const REACTIONS_H = 38  // 🔥 ☕ 👀 quick-reaction pills
    const DATE_H = 24       // post date
    const CARD_PAD_BOT = 14
    const FOOTER_H = 68

    const CARD_H =
      CARD_PAD_TOP + AVATAR_ROW_H + DRINK_ROW_H + MUG_ROW_H +
      BADGE_ROW_H + PHOTO_H + TAGS_H + CAPTION_H + SHOP_H +
      ACTIONS_H + REACTIONS_H + DATE_H + CARD_PAD_BOT

    const TOTAL_H = HEADER_H + 16 + CARD_H + 16 + FOOTER_H

    // ── Set up canvas at SCALE resolution ───────────────────
    canvas.width = W * SCALE
    canvas.height = TOTAL_H * SCALE
    const ctx = canvas.getContext('2d')!
    ctx.scale(SCALE, SCALE)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Truncate text with an ellipsis so it never exceeds maxW px (uses current ctx.font)
    const fitText = (text: string, maxW: number): string => {
      if (ctx.measureText(text).width <= maxW) return text
      let t = text
      while (t.length > 1 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1)
      return t + '…'
    }

    // ── Background ──────────────────────────────────────────
    const bg = ctx.createLinearGradient(0, 0, 0, TOTAL_H)
    bg.addColorStop(0, '#fdfaf5')
    bg.addColorStop(1, '#faf6ef')
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, TOTAL_H)

    // ── Header ───────────────────────────────────────────────
    let Y = 0
    // Cream background matching the app header
    ctx.fillStyle = '#fdfaf5'
    ctx.fillRect(0, Y, W, HEADER_H)
    // Subtle bottom border
    ctx.strokeStyle = '#e8d8c0'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, Y + HEADER_H)
    ctx.lineTo(W, Y + HEADER_H)
    ctx.stroke()

    // Social Brew logo — rounded square, matching the app icon
    const iconCX = PAD + 22, iconCY = Y + HEADER_H / 2
    const LOGO = 44, logoX = iconCX - LOGO / 2, logoY = iconCY - LOGO / 2
    const LOGO_R = 12
    if (logoImg) {
      ctx.save()
      roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
      ctx.clip()
      ctx.drawImage(logoImg, logoX, logoY, LOGO, LOGO)
      ctx.restore()
    } else {
      // Fallback: dark rounded square with a cup, mirroring the icon
      roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
      ctx.fillStyle = '#1c1008'
      ctx.fill()
      ctx.font = '22px system-ui'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = '#c8a068'
      ctx.fillText('☕', iconCX, iconCY + 1)
      ctx.textBaseline = 'alphabetic'
      ctx.textAlign = 'left'
    }
    // Soft border to lift the logo off the cream header
    roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
    ctx.strokeStyle = '#e8d8c0'
    ctx.lineWidth = 1
    ctx.stroke()

    ctx.font = 'bold 21px Georgia, serif'
    ctx.fillStyle = '#1c0a02'
    ctx.fillText('Social Brew', PAD + 54, Y + HEADER_H / 2 - 2)
    ctx.font = '12px system-ui, -apple-system, sans-serif'
    ctx.fillStyle = '#9b7a55'
    ctx.fillText('Independent coffee only', PAD + 54, Y + HEADER_H / 2 + 17)

    Y += HEADER_H + 16

    // ── Card shadow + background ─────────────────────────────
    ctx.save()
    ctx.shadowColor = 'rgba(28,10,2,0.12)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetY = 6
    roundRect(ctx, CARD_MX, Y, CARD_W, CARD_H, CARD_R)
    ctx.fillStyle = 'white'
    ctx.fill()
    ctx.restore()

    // ── Card content ─────────────────────────────────────────
    const CX = CARD_MX
    let CY = Y + CARD_PAD_TOP

    // Avatar + username + badge + time
    const avCX = CX + PAD + AVATAR_R
    const avCY = CY + AVATAR_R
    drawAvatar(
      ctx, avatarImg, avCX, avCY, AVATAR_R,
      user?.username?.[0] || '?', liquidColor
    )

    const textX = CX + PAD + AVATAR_R * 2 + 10
    ctx.font = `bold 14px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#1c0a02'
    ctx.fillText(user?.username || '', textX, CY + 14)

    // Top row: verified badge + First Brew + visit-time pill (mirrors the feed)
    const usernameW = ctx.measureText(user?.username || '').width
    let afterNameX = textX + usernameW

    if (user?.verified) {
      const vbSize = 15
      drawVerifiedBadge(ctx, afterNameX + 5 + vbSize / 2, CY + 9, vbSize)
      afterNameX += 5 + vbSize
    }

    // Small pill helper for the header row
    function headerPill(label: string, bg: string, border: string, text: string) {
      ctx.font = `10px system-ui, -apple-system, sans-serif`
      const px = afterNameX + 8
      const maxRight = CX + CARD_W - PAD
      if (px + 24 > maxRight) return // no room for even a tiny pill — skip
      const lbl = fitText(label, maxRight - px - 14)
      const pw = ctx.measureText(lbl).width + 14
      roundRect(ctx, px, CY + 1, pw, 17, 8.5)
      ctx.fillStyle = bg; ctx.fill()
      ctx.strokeStyle = border; ctx.lineWidth = 0.8; ctx.stroke()
      ctx.fillStyle = text
      ctx.fillText(lbl, px + 7, CY + 13)
      afterNameX = px + pw
    }

    if (rating.is_first_rating) headerPill('⭐ First Brew', '#fef3c7', '#fde68a', '#b45309')
    if (rating.visit_time) headerPill(`🕐 ${rating.visit_time}`, '#f5ede0', '#e8d8c0', '#9b7a55')

    // Second line: time since posted
    if (rating.created_at) {
      ctx.font = `11px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a55'
      ctx.fillText(timeAgo(rating.created_at), textX, CY + 32)
    }

    CY += AVATAR_ROW_H

    // Drink name pill
    if (rating.drink_name || showPrice) {
      let drinkTx = CX + PAD
      const maxRight = CX + CARD_W - PAD
      ctx.font = `11px system-ui, -apple-system, sans-serif`

      if (rating.drink_name) {
        ctx.fillStyle = '#9b7a55'
        ctx.fillText('ordered', drinkTx, CY + 17)
        drinkTx += ctx.measureText('ordered').width + 8

        // Size pill — light, sits between "ordered" and the drink name.
        // Guarded so it never crosses the card's right edge.
        if (rating.drink_size) {
          ctx.font = `11px system-ui, -apple-system, sans-serif`
          const szW = ctx.measureText(rating.drink_size).width + 16
          if (drinkTx + szW <= maxRight) {
            roundRect(ctx, drinkTx, CY + 5, szW, 22, 11)
            ctx.fillStyle = '#f6efe4'; ctx.fill()
            ctx.fillStyle = '#9b7a55'
            ctx.fillText(rating.drink_size, drinkTx + 8, CY + 20)
            drinkTx += szW + 6
          }
        }

        // Reserve room for the price / worth-it pills that follow, so a long
        // drink name truncates instead of pushing them off the card edge.
        let reserved = 0
        if (showPrice && rating.drink_price && rating.drink_price > 0) {
          ctx.font = `bold 11px system-ui, -apple-system, sans-serif`
          reserved += ctx.measureText(`$${(rating.drink_price as number).toFixed(2)}`).width + 16 + 6
        }
        if (showPrice && rating.price_perception) {
          const pl: Record<string, string> = { steal: 'Steal 🤑', worth_it: 'Worth it ✓', overpriced: 'Overpriced 😬' }
          const l = pl[rating.price_perception]
          if (l) { ctx.font = `bold 11px system-ui, -apple-system, sans-serif`; reserved += ctx.measureText(l).width + 18 + 6 }
        }

        ctx.font = `12px system-ui, -apple-system, sans-serif`
        const drinkName = fitText(rating.drink_name, Math.max(40, maxRight - drinkTx - 22 - reserved))
        const dpW = ctx.measureText(drinkName).width + 22
        roundRect(ctx, drinkTx, CY + 5, dpW, 22, 11)
        ctx.fillStyle = '#f0e8dc'
        ctx.fill()
        ctx.fillStyle = '#1c0a02'
        ctx.fillText(drinkName, drinkTx + 11, CY + 20)
        drinkTx += dpW + 8
      }

      // Price pill — guarded so it never crosses the card's right edge
      if (showPrice && rating.drink_price && rating.drink_price > 0) {
        ctx.font = `bold 11px system-ui, -apple-system, sans-serif`
        const priceStr = `$${(rating.drink_price as number).toFixed(2)}`
        const priceW = ctx.measureText(priceStr).width + 16
        if (drinkTx + priceW <= maxRight) {
          roundRect(ctx, drinkTx, CY + 5, priceW, 22, 11)
          ctx.fillStyle = '#f5ead8'; ctx.fill()
          ctx.strokeStyle = '#e8d4b0'; ctx.lineWidth = 0.8; ctx.stroke()
          ctx.fillStyle = '#7a5c3a'
          ctx.fillText(priceStr, drinkTx + 8, CY + 20)
          drinkTx += priceW + 6
        }
      }

      // Worth-it pill — guarded so it never crosses the card's right edge
      if (showPrice && rating.price_perception) {
        const pm: Record<string, {label: string; bg: string; text: string; border: string}> = {
          steal:      { label: 'Steal 🤑',      bg: '#dcfce7', text: '#15803d', border: '#86efac' },
          worth_it:   { label: 'Worth it ✓',    bg: '#fdf0dc', text: '#c8853a', border: '#c8853a' },
          overpriced: { label: 'Overpriced 😬', bg: '#fef2f2', text: '#dc2626', border: '#fca5a5' },
        }
        const p = pm[rating.price_perception]
        if (p) {
          ctx.font = `bold 11px system-ui, -apple-system, sans-serif`
          const pw = ctx.measureText(p.label).width + 18
          if (drinkTx + pw <= maxRight) {
            roundRect(ctx, drinkTx, CY + 5, pw, 22, 11)
            ctx.fillStyle = p.bg; ctx.fill()
            ctx.strokeStyle = p.border; ctx.lineWidth = 0.8; ctx.stroke()
            ctx.fillStyle = p.text
            ctx.fillText(p.label, drinkTx + 9, CY + 20)
          }
        }
      }

      CY += DRINK_ROW_H
    }

    // Mug + rating label + fill bar
    if (!isVibePost && fill > 0) {
      drawMug(ctx, CX + PAD, CY, MUG_SIZE, fill)

      const labelX = CX + PAD + MUG_SIZE + 18
      const labelY = CY + 22

      ctx.font = `bold 17px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#3d2010'
      ctx.fillText(fillLabel, labelX, labelY)

      // Fill bar
      const barX = labelX
      const barY = labelY + 12
      const barW = CARD_W - PAD - MUG_SIZE - 18 - PAD - 32
      const barH = 8
      roundRect(ctx, barX, barY, barW, barH, barH / 2)
      ctx.fillStyle = '#f0e4d0'
      ctx.fill()
      roundRect(ctx, barX, barY, barW * (fill / 100), barH, barH / 2)
      const fillGrad = ctx.createLinearGradient(barX, 0, barX + barW * (fill / 100), 0)
      fillGrad.addColorStop(0, liquidColor + '88')
      fillGrad.addColorStop(1, liquidColor)
      ctx.fillStyle = fillGrad
      ctx.fill()

      // Percentage
      ctx.font = `12px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a55'
      ctx.fillText(`${fill}%`, barX + barW + 6, barY + barH - 1)

      CY += MUG_ROW_H
    }

    // Quick Sip / Vibe Check badge
    if (isQuickSip) {
      const pillW = 90
      roundRect(ctx, CX + PAD, CY + 7, pillW, 22, 11)
      ctx.fillStyle = '#eff6ff'
      ctx.fill()
      ctx.font = `11px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#3b82f6'
      ctx.fillText('⚡ Quick Sip', CX + PAD + 8, CY + 22)
      CY += BADGE_ROW_H
    } else if (isVibePost) {
      const pillW = 100
      roundRect(ctx, CX + PAD, CY + 7, pillW, 22, 11)
      ctx.fillStyle = '#f5e6d0'
      ctx.fill()
      ctx.font = `11px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#c8853a'
      ctx.fillText('✨ Vibe Check', CX + PAD + 8, CY + 22)
      CY += BADGE_ROW_H
    }

    // Photos — each drawn in its own save/restore with its own clip
    // No outer clip needed — white card background hides any overflow
    if (loadedPhotos.length > 0 && PHOTO_H > 0) {

      function drawPhoto(
        img: HTMLImageElement,
        px: number, py: number,
        pw: number, ph: number,
        fit: 'cover' | 'contain' = 'cover'
      ) {
        const scaleW = pw / img.naturalWidth
        const scaleH = ph / img.naturalHeight
        // cover fills the box (may crop); contain fits the whole image (no crop)
        const ratio = fit === 'contain'
          ? Math.min(scaleW, scaleH)
          : Math.max(scaleW, scaleH)
        const dw = img.naturalWidth * ratio
        const dh = img.naturalHeight * ratio
        const dx = px + (pw - dw) / 2
        const dy = py + (ph - dh) / 2
        ctx.save()
        ctx.beginPath()
        ctx.rect(px, py, pw, ph)
        ctx.clip()
        // Dark backdrop behind letterboxed (contain) photos, like the feed
        if (fit === 'contain') {
          ctx.fillStyle = '#0a0605'
          ctx.fillRect(px, py, pw, ph)
        }
        ctx.drawImage(img, dx, dy, dw, dh)
        ctx.restore()
      }

      const GAP = 2 // white gap between photos

      if (loadedPhotos.length === 1) {
        // Single photo shown in full (no cropping), matching the feed
        drawPhoto(loadedPhotos[0], CX, CY, CARD_W, PHOTO_H, 'contain')

      } else if (loadedPhotos.length === 2) {
        // Each photo shown in full (contain), so nothing is cut off
        const colW = (CARD_W - GAP) / 2
        drawPhoto(loadedPhotos[0], CX, CY, colW, PHOTO_H, 'contain')
        drawPhoto(loadedPhotos[1], CX + colW + GAP, CY, colW, PHOTO_H, 'contain')

      } else if (loadedPhotos.length === 3) {
        const topH = Math.floor(PHOTO_H * 0.56)
        const botH = PHOTO_H - topH - GAP
        const colW = (CARD_W - GAP) / 2
        drawPhoto(loadedPhotos[0], CX, CY, CARD_W, topH, 'contain')
        drawPhoto(loadedPhotos[1], CX, CY + topH + GAP, colW, botH, 'contain')
        drawPhoto(loadedPhotos[2], CX + colW + GAP, CY + topH + GAP, colW, botH, 'contain')

      } else {
        // 4 photos: equal 2x2 grid, each shown in full (contain)
        const colW = (CARD_W - GAP) / 2
        const rowH = (PHOTO_H - GAP) / 2
        drawPhoto(loadedPhotos[0], CX,           CY,           colW, rowH, 'contain')
        drawPhoto(loadedPhotos[1], CX + colW + GAP, CY,        colW, rowH, 'contain')
        drawPhoto(loadedPhotos[2], CX,           CY + rowH + GAP, colW, rowH, 'contain')
        drawPhoto(loadedPhotos[3], CX + colW + GAP, CY + rowH + GAP, colW, rowH, 'contain')
      }

      CY += PHOTO_H
    }

    // Vibe tags
    if (rating.vibe_tags && rating.vibe_tags.length > 0) {
      let tx = CX + PAD
      rating.vibe_tags.forEach(tag => {
        ctx.font = `11px system-ui, -apple-system, sans-serif`
        const tw = ctx.measureText(tag).width + 18
        if (tx + tw > CX + CARD_W - PAD) return // skip if overflows
        roundRect(ctx, tx, CY + 10, tw, 22, 11)
        ctx.fillStyle = '#f5ead8'
        ctx.fill()
        ctx.strokeStyle = '#e8d4b0'
        ctx.lineWidth = 0.8
        ctx.stroke()
        ctx.fillStyle = '#7a5c3a'
        ctx.fillText(tag, tx + 9, CY + 25)
        tx += tw + 8
      })
      CY += TAGS_H
    }

    // Caption — max 6 lines
    if (captionLines.length > 0) {
      ctx.font = `13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#3d2010'
      const maxLines = Math.min(captionLines.length, 6)
      for (let i = 0; i < maxLines; i++) {
        ctx.fillText(captionLines[i], CX + PAD, CY + 16 + i * 22)
      }
      if (captionLines.length > 6) {
        ctx.fillStyle = '#9b7a55'
        ctx.font = `12px system-ui, -apple-system, sans-serif`
        ctx.fillText('...', CX + PAD, CY + 16 + 6 * 22)
      }
      CY += CAPTION_H
    }

    // Shop card
    if (shop) {
      const shopCardY = CY + 8
      const shopCardH = 44
      roundRect(ctx, CX + PAD, shopCardY, CARD_W - PAD * 2, shopCardH, 12)
      ctx.fillStyle = '#fdfaf5'
      ctx.fill()
      ctx.strokeStyle = '#e8d4b0'
      ctx.lineWidth = 1
      ctx.stroke()

      // Shop thumbnail (matches the feed's shop card)
      const thumbS = 32
      const thumbX = CX + PAD + 8
      const thumbY = shopCardY + (shopCardH - thumbS) / 2
      ctx.save()
      roundRect(ctx, thumbX, thumbY, thumbS, thumbS, 8)
      ctx.clip()
      if (shopImg) {
        const ratio = Math.max(thumbS / shopImg.naturalWidth, thumbS / shopImg.naturalHeight)
        const dw = shopImg.naturalWidth * ratio
        const dh = shopImg.naturalHeight * ratio
        ctx.drawImage(shopImg, thumbX + (thumbS - dw) / 2, thumbY + (thumbS - dh) / 2, dw, dh)
      } else {
        ctx.fillStyle = '#e8ddc8'
        ctx.fillRect(thumbX, thumbY, thumbS, thumbS)
      }
      ctx.restore()

      const shopTextX = thumbX + thumbS + 10
      ctx.font = `bold 13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#1c0a02'
      ctx.fillText(shop.name, shopTextX, shopCardY + 18)

      if (location) {
        ctx.font = `11px system-ui, -apple-system, sans-serif`
        ctx.fillStyle = '#9b7a55'
        ctx.fillText(location, shopTextX, shopCardY + 34)
      }

      ctx.font = `bold 12px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#c8853a'
      ctx.textAlign = 'right'
      ctx.fillText('View', CX + CARD_W - PAD, shopCardY + 26)
      ctx.textAlign = 'left'
    }

    if (shop) CY += SHOP_H

    // Action row — like / comment / reaction / wishlist ... save / share
    {
      const iconY = CY + 20
      const likes = rating.likes_count || 0
      const comments = rating.comments_count || 0

      ctx.textBaseline = 'middle'

      // Left cluster
      let ax = CX + PAD + 9
      drawHeart(ctx, ax, iconY, 19, '#9b7a45')
      ax += 14
      ctx.font = `bold 13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a45'
      ctx.fillText(String(likes), ax, iconY + 1)
      ax += ctx.measureText(String(likes)).width + 18

      drawComment(ctx, ax, iconY, 19, '#7a5c3a')
      ax += 14
      ctx.font = `13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#7a5c3a'
      ctx.fillText(String(comments), ax, iconY + 1)
      ax += ctx.measureText(String(comments)).width + 18

      // Reaction indicator
      ctx.font = `15px system-ui`
      ctx.fillText('☕', ax, iconY + 1)
      ax += 24

      // Right cluster: save · share (drawn right-to-left)
      let rx = CX + CARD_W - PAD - 9
      // Left edge of the right cluster — used to keep Wishlist from colliding
      const rightClusterLeft = rx - 1 * 28 - 12

      // Wishlist — only draw its label if there's room before the right cluster
      drawPlus(ctx, ax + 2, iconY, 16, '#9b7a55')
      ax += 12
      ctx.font = `12px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a55'
      if (ax + 5 + ctx.measureText('Wishlist').width <= rightClusterLeft) {
        ctx.fillText('Wishlist', ax + 5, iconY + 1)
      }

      drawShareIcon(ctx, rx, iconY, 17, '#9b7a55'); rx -= 28
      drawBookmark(ctx, rx, iconY, 17, '#9b7a45')

      ctx.textBaseline = 'alphabetic'
      CY += ACTIONS_H
    }

    // Quick-reaction pills (🔥 ☕ 👀)
    {
      const pillY = CY + 4
      let px = CX + PAD
      ctx.font = `13px system-ui, -apple-system, sans-serif`
      ctx.textBaseline = 'middle'
      for (const emoji of ['🔥', '☕', '👀']) {
        const pw = ctx.measureText(emoji).width + 22
        roundRect(ctx, px, pillY, pw, 26, 13)
        ctx.fillStyle = 'rgba(200,133,58,0.08)'
        ctx.fill()
        ctx.strokeStyle = 'rgba(200,133,58,0.22)'
        ctx.lineWidth = 1
        ctx.stroke()
        ctx.fillStyle = '#9b7a45'
        ctx.fillText(emoji, px + 11, pillY + 14)
        px += pw + 8
      }
      ctx.textBaseline = 'alphabetic'
      CY += REACTIONS_H
    }

    // Post date
    if (rating.created_at) {
      ctx.font = `11px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#b8a488'
      ctx.fillText(formatDate(rating.created_at), CX + PAD, CY + 12)
    }
    CY += DATE_H

    // ── Footer ───────────────────────────────────────────────
    const footerY = Y + CARD_H + 16
    ctx.font = `12px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#9b7a55'
    ctx.textAlign = 'center'
    ctx.fillText('Discover independent coffee shops', W / 2, footerY + 20)
    ctx.font = `bold 13px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#c8853a'
    ctx.fillText(`Join Social Brew → ${APP_URL}`, W / 2, footerY + 42)
    ctx.textAlign = 'left'

    // ── Export ───────────────────────────────────────────────
    canvas.toBlob(b => {
      if (b) {
        setBlob(b)
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev)
          return URL.createObjectURL(b)
        })
      } else {
        setStatus('Could not generate card — try again')
      }
      setGenerating(false)
    }, 'image/png', 0.95)
  }

  async function share() {
    if (!blob) return
    setSharing(true)
    setStatus(null)
    const file = new File([blob], 'social-brew.png', { type: 'image/png' })

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `${shop?.name} on Social Brew` })
        setSharing(false); return
      } catch (e: any) {
        if (e.name === 'AbortError') { setSharing(false); return }
      }
    }

    if (/iphone|ipad/i.test(navigator.userAgent)) {
      download()
      window.location.href = 'instagram-stories://share'
      setTimeout(() => {
        setStatus('Image saved — open Instagram → tap + → Story → select from Photos')
        setSharing(false)
      }, 1500)
      return
    }

    download()
    setStatus('Image saved — open Instagram, tap + → Story → select from gallery')
    setSharing(false)
  }

  function download() {
    if (!previewUrl) return
    const a = document.createElement('a')
    a.href = previewUrl
    a.download = `social-brew-${(shop?.name || 'post').replace(/\s+/g, '-').toLowerCase()}.png`
    a.click()
  }

  return (
    <div className="fixed inset-0 z-[300] flex flex-col items-center justify-end"
      style={{ background: 'rgba(0,0,0,0.92)' }} onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-t-3xl pb-8"
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="font-bold text-coffee-800 text-base">Share to Instagram Stories</h3>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-full bg-cream-100 flex items-center justify-center">
            <X size={15} className="text-coffee-500" />
          </button>
        </div>

        {/* Preview */}
        <div className="mx-5 mb-4 rounded-2xl overflow-hidden border border-cream-200 bg-cream-50"
          style={{ height: 280 }}>
          {generating ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <div className="w-6 h-6 rounded-full border-2 border-caramel border-t-transparent animate-spin" />
              <p className="text-coffee-400 text-xs">Building your card...</p>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-coffee-300 text-xs text-center px-4">{status || 'Preview unavailable'}</p>
            </div>
          )}
        </div>

        {status && previewUrl && (
          <p className="mx-5 mb-3 text-coffee-500 text-xs text-center leading-relaxed bg-cream-50 rounded-xl px-3 py-2">
            {status}
          </p>
        )}

        <div className="px-5 flex flex-col gap-2">
          <button onClick={share} disabled={generating || sharing || !blob}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)' }}>
            <Share2 size={17} />
            {sharing ? 'Opening...' : 'Share to Instagram Stories'}
          </button>
          <button onClick={download} disabled={generating || !blob}
            className="w-full py-3 rounded-2xl bg-cream-100 text-coffee-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            <Download size={15} />
            Save image instead
          </button>
        </div>

        <p className="px-5 pt-3 text-coffee-300 text-xs text-center">
          Your followers can visit the link to join Social Brew ☕
        </p>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  )
}
